import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMode } from '../contexts/ModeContext';
import { HardHat, ShieldCheck } from '@phosphor-icons/react';

const MODE_CONFIG = {
  opc: { icon: HardHat, label: 'OPC', bg: 'bg-[#0055FF]', lightBg: 'bg-blue-100 border-blue-200', textColor: 'text-[#0055FF]', desc: 'Coordination & Pilotage chantier', homePath: '/dashboard', switchTarget: '/csps/chantiers' },
  csps: { icon: ShieldCheck, label: 'CSPS', bg: 'bg-[#FF6B00]', lightBg: 'bg-orange-100 border-orange-200', textColor: 'text-[#FF6B00]', desc: 'Sécurité & Protection sante', homePath: '/csps/chantiers', switchTarget: '/dashboard' },
};

const BANNER_CONFIG = {
  opc: { wrapperBg: 'bg-blue-50 border border-blue-200', iconColor: 'text-[#0055FF]', subtitleColor: 'text-blue-600', subtitle: 'Ordonnancement, Pilotage, Coordination' },
  csps: { wrapperBg: 'bg-orange-50 border border-orange-200', iconColor: 'text-[#FF6B00]', subtitleColor: 'text-orange-600', subtitle: 'Coordination Sécurité et Protection de la Sante' },
};

export function ModeIndicator({ variant = 'full' }) {
  const { mode, toggleMode, isManualMode, resetToAutoMode, canSwitch } = useMode();
  const navigate = useNavigate();

  const current = MODE_CONFIG[mode];
  const Icon = current.icon;
  const isOPC = mode === 'opc';

  const handleModeClick = () => {
    if (!canSwitch) return;
    toggleMode();
    navigate(current.switchTarget);
  };

  if (variant === 'compact') {
    return (
      <button onClick={handleModeClick} disabled={!canSwitch}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${current.lightBg} ${current.textColor} border-2 ${!canSwitch ? 'opacity-80 cursor-default' : ''}`}
        data-testid="mode-indicator-compact">
        <Icon size={16} weight="fill" />
        <span>{current.label}</span>
      </button>
    );
  }

  return (
    <div className="px-3 py-2 mx-3 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-medium text-[#9CA3AF] uppercase tracking-wider">Mode actif</span>
        {isManualMode && canSwitch && (
          <button onClick={resetToAutoMode} className="text-[10px] text-[#9CA3AF] hover:text-[#111827] underline">Auto</button>
        )}
      </div>

      {canSwitch ? (
        <div className="flex rounded-md bg-white border border-gray-200 p-0.5" data-testid="mode-switch">
          {Object.entries(MODE_CONFIG).map(([key, cfg]) => {
            const active = mode === key;
            return (
              <button key={key}
                onClick={() => { if (!active) handleModeClick(); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded text-xs font-semibold transition-all ${active ? `${cfg.bg} text-white shadow-sm` : 'text-[#4B5563] hover:bg-gray-50'}`}
                data-testid={`mode-${key}-btn`}>
                <cfg.icon size={14} weight={active ? 'fill' : 'regular'} />
                <span>{cfg.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold ${current.bg} text-white`} data-testid="mode-single">
          <Icon size={14} weight="fill" />
          <span>{current.label}</span>
        </div>
      )}

      <p className="mt-1 text-[10px] text-[#9CA3AF] text-center">{current.desc}</p>
    </div>
  );
}

export function ModeBanner() {
  const { mode } = useMode();
  const cfg = BANNER_CONFIG[mode];
  const Icon = MODE_CONFIG[mode].icon;

  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg mb-4 ${cfg.wrapperBg}`} data-testid="mode-banner">
      <Icon size={20} className={cfg.iconColor} weight="fill" />
      <span className={`text-sm font-semibold ${cfg.iconColor}`}>Mode {MODE_CONFIG[mode].label}</span>
      <span className={`text-sm ${cfg.subtitleColor}`}>— {cfg.subtitle}</span>
    </div>
  );
}
